'use client';

import { Suspense, useEffect, useRef, useCallback } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

/** Network-aware safety timeout */
function getProgressTimeout(): number {
  const conn = (navigator as any).connection;
  const ect: string = conn?.effectiveType || '4g';
  if (ect === '2g' || ect === 'slow-2g') return 20000;
  if (ect === '3g') return 15000;
  return 8000;
}

/**
 * Inner component that uses useSearchParams (requires Suspense boundary).
 */
function NavigationProgressBar() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const barRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const animRef = useRef<ReturnType<typeof setInterval>>(undefined);
  const safetyTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const widthRef = useRef(0);
  const isActiveRef = useRef(false);
  const prevPathRef = useRef(pathname);
  const prevSearchRef = useRef(searchParams.toString());

  const completeProgress = useCallback(() => {
    const bar = barRef.current;
    if (!bar) return;

    isActiveRef.current = false;
    if (animRef.current) clearInterval(animRef.current);
    if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);

    // Quick fill to 100%
    bar.style.transition = 'width 200ms ease-out';
    bar.style.width = '100%';

    // Fade out
    timerRef.current = setTimeout(() => {
      bar.style.transition = 'opacity 300ms ease-out';
      bar.style.opacity = '0';
      timerRef.current = setTimeout(() => {
        bar.style.width = '0%';
        widthRef.current = 0;
      }, 300);
    }, 200);
  }, []);

  const startProgress = useCallback(() => {
    const bar = barRef.current;
    if (!bar) return;

    // Clear any existing timers
    if (timerRef.current) clearTimeout(timerRef.current);
    if (animRef.current) clearInterval(animRef.current);
    if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);

    isActiveRef.current = true;

    // Reset and show
    widthRef.current = 0;
    bar.style.opacity = '1';
    bar.style.width = '0%';
    bar.style.transition = 'none';

    // Animate: fast to 30%, then slow crawl to 90%
    requestAnimationFrame(() => {
      bar.style.transition = 'width 300ms ease-out';
      bar.style.width = '30%';
      widthRef.current = 30;

      animRef.current = setInterval(() => {
        if (widthRef.current < 90) {
          widthRef.current += (90 - widthRef.current) * 0.08;
          bar.style.width = `${widthRef.current}%`;
        }
      }, 200);
    });

    // Safety timeout: auto-complete if navigation doesn't finish
    safetyTimerRef.current = setTimeout(() => {
      if (isActiveRef.current) {
        completeProgress();
      }
    }, getProgressTimeout());
  }, [completeProgress]);

  // Navigation complete - finish progress bar when pathname or searchParams change
  useEffect(() => {
    const currentSearch = searchParams.toString();
    if (pathname !== prevPathRef.current || currentSearch !== prevSearchRef.current) {
      prevPathRef.current = pathname;
      prevSearchRef.current = currentSearch;
      if (isActiveRef.current) {
        completeProgress();
      }
    }
  }, [pathname, searchParams, completeProgress]);

  // Intercept link clicks to detect navigation start
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement).closest('a');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href) return;

      // Skip external links, hash links, new tab links
      if (
        href.startsWith('http') ||
        href.startsWith('#') ||
        href.startsWith('mailto:') ||
        href.startsWith('tel:') ||
        anchor.target === '_blank' ||
        e.ctrlKey || e.metaKey || e.shiftKey
      ) {
        return;
      }

      // Skip if navigating to same page
      const currentPath = window.location.pathname + window.location.search;
      if (href === currentPath) return;

      startProgress();
    };

    // Handle browser back/forward
    const handlePopState = () => {
      // Check if the browser URL already matches current React state
      // (bfcache restore or instant client cache hit)
      const browserPath = window.location.pathname;
      if (browserPath === prevPathRef.current) {
        return;
      }
      startProgress();
    };

    // Handle bfcache restore: dismiss any stale progress
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted && isActiveRef.current) {
        completeProgress();
      }
    };

    document.addEventListener('click', handleClick, { capture: true });
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      document.removeEventListener('click', handleClick, { capture: true });
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('pageshow', handlePageShow);
      if (timerRef.current) clearTimeout(timerRef.current);
      if (animRef.current) clearInterval(animRef.current);
      if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
    };
  }, [startProgress, completeProgress]);

  return (
    <div
      ref={barRef}
      aria-hidden="true"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        height: '3px',
        width: '0%',
        backgroundColor: '#D32F2F',
        zIndex: 99999,
        opacity: 0,
        pointerEvents: 'none',
        boxShadow: '0 0 8px rgba(211, 47, 47, 0.4)',
      }}
    />
  );
}

/**
 * Global navigation progress bar for page transitions.
 * Shows a thin red animated bar at the top during client-side navigation.
 * Wrapped in Suspense because useSearchParams requires it.
 */
export default function NavigationProgress() {
  return (
    <Suspense fallback={null}>
      <NavigationProgressBar />
    </Suspense>
  );
}
